import React, { useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { brl, fileUrl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEW = "https://g.page/r/CSAnGGabR5n5EBM/review";
const fmt = (d) => (d ? d.split("-").reverse().join("/") : "—");
const ST = {
  pago: { l: "Pago", i: "✅", c: "#5fa86c" },
  pendente: { l: "Pendente", i: "📅", c: "#d4b27a" },
  atrasado: { l: "Em atraso", i: "⚠️", c: "#d46a6a" },
};
const Sec = ({ titulo, code, testid, children }) => (
  <div className="sb-section" data-testid={testid}>
    <div className="sb-h" style={{ gap: 16, flexWrap: "wrap" }}><div className="sb-h-title">{titulo}</div>{code && <div className="sb-h-code">{code}</div>}</div>
    {children}
  </div>
);

export function SBCronogramaPagamentos({ itens }) {
  if (!itens || itens.length === 0) return null;
  return (
    <Sec titulo="Cronograma de pagamentos" code={`${itens.length} parcela(s)`} testid="sb-cronograma-pag">
      <div style={{ display: "flex", flexDirection: "column" }}>
        {itens.map((p, i) => {
          const s = ST[p.status] || ST.pendente;
          return (
            <div key={i} data-testid={`sb-parcela-${i}`} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: "4px 16px", alignItems: "center", padding: "14px 0", borderTop: i ? "1px solid var(--line)" : "none" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: 16, color: "var(--ink)" }}>{p.descricao}</div>
                <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 3 }}>Vencimento {fmt(p.data)}{p.metodo ? ` · ${p.metodo}` : ""}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink)", fontWeight: 600 }}>{brl(p.valor)}</div>
                <div style={{ fontSize: 11, color: s.c, marginTop: 3 }} data-testid={`sb-parcela-status-${i}`}>{s.i} {s.l}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Sec>
  );
}

export function SBTransformacao({ fotos }) {
  const ok = (f) => f.aprovada !== false && f.type !== "video";
  const amb = (f) => (f.ambiente || "").trim() || "A obra";
  const antes = {}, depois = {};
  (fotos || []).filter(ok).forEach(f => {
    if (f.antes_obra) (antes[amb(f)] = antes[amb(f)] || []).push(f);
    else if (f.obra_concluida) (depois[amb(f)] = depois[amb(f)] || []).push(f);
  });
  let pares = Object.keys(antes).filter(a => depois[a]).map(a => [a, antes[a][0], depois[a][0]]);
  const totA = Object.values(antes).flat(), totD = Object.values(depois).flat();
  if (pares.length === 0 && totA.length && totD.length) pares = [["A obra", totA[0], totD[0]]];
  if (pares.length === 0) return null;
  const Foto = ({ f, rot }) => (
    <figure style={{ margin: 0, position: "relative" }}>
      <img src={fileUrl(f.url)} alt={rot} loading="lazy" style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", display: "block", border: "1px solid var(--gold-3)", filter: rot === "Antes" ? "saturate(0.75)" : "none" }} />
      <span className="sb-timeline-kind" style={{ left: 8, right: "auto", background: rot === "Antes" ? "#3a3740" : "var(--gold)", color: rot === "Antes" ? "#f5f0e8" : "#0d0d0f" }}>{rot}</span>
    </figure>
  );
  return (
    <Sec titulo="Transformação" code={`${pares.length} ambiente(s)`} testid="sb-transformacao">
      <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
        {pares.map(([a, fa, fd]) => (
          <div key={a} data-testid={`sb-transf-${a}`}>
            <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 17, color: "var(--ink)", marginBottom: 10 }}>{a}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,260px),1fr))", gap: 12 }}>
              <Foto f={fa} rot="Antes" /><Foto f={fd} rot="Depois" />
            </div>
          </div>
        ))}
      </div>
    </Sec>
  );
}

const CAMPOS = [["origem", "Origem"], ["composicao", "Composição"], ["acabamentos", "Acabamentos"], ["indicacoes", "Indicações de uso"], ["cuidados", "Cuidados"]];

export function SBFichasTecnicas({ fichas }) {
  if (!fichas || fichas.length === 0) return null;
  return (
    <Sec titulo="Ficha técnica dos materiais" code={`${fichas.length} material(is)`} testid="sb-fichas">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,340px),1fr))", gap: 18 }}>
        {fichas.map(f => (
          <div key={f.material_id} data-testid={`sb-ficha-${f.material_id}`} style={{ border: "1px solid var(--gold-3)", padding: "20px 22px", background: "linear-gradient(180deg, rgba(184,147,90,0.06), transparent)" }}>
            <div style={{ fontSize: 9, letterSpacing: ".3em", textTransform: "uppercase", color: "var(--gold)" }}>{f.categoria || "Material"}</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 22, color: "var(--ink)", margin: "6px 0 8px" }}>{f.nome}</div>
            {f.conteudo.resumo && <p style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.6, margin: "0 0 12px" }}>{f.conteudo.resumo}</p>}
            {CAMPOS.filter(([k]) => f.conteudo[k]).map(([k, l]) => (
              <div key={k} style={{ padding: "8px 0", borderTop: "1px solid var(--line)" }}>
                <div style={{ fontSize: 9, letterSpacing: ".25em", textTransform: "uppercase", color: "var(--ink-3)" }}>{l}</div>
                <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 3, lineHeight: 1.55, overflowWrap: "anywhere" }}>{f.conteudo[k]}</div>
              </div>
            ))}
            {f.fontes.length > 0 && (
              <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 8, overflowWrap: "anywhere" }}>
                Fontes: {f.fontes.slice(0, 3).map((u, i) => <a key={u} href={u} target="_blank" rel="noreferrer" style={{ color: "var(--gold)", marginRight: 6 }}>[{i + 1}] {u.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}</a>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </Sec>
  );
}

export function SBValorizacao({ v }) {
  if (!v || !v.texto) return null;
  return (
    <Sec titulo="Valorização estimada do imóvel" testid="sb-valorizacao">
      <div style={{ padding: "22px 24px", border: "1px solid var(--gold-3)", background: "rgba(184,147,90,0.05)" }}>
        {v.faixa_min != null && v.faixa_max != null && (
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 30, color: "var(--gold)", marginBottom: 8 }} data-testid="sb-valorizacao-faixa">entre {v.faixa_min}% e {v.faixa_max}%</div>
        )}
        <p style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 16, color: "var(--ink)", lineHeight: 1.6, margin: 0 }} data-testid="sb-valorizacao-texto">{v.texto}</p>
        <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 12 }}>
          Fonte: {v.fonte_url ? <a href={v.fonte_url} target="_blank" rel="noreferrer" style={{ color: "var(--gold)" }} data-testid="sb-valorizacao-fonte">{v.fonte_nome || v.fonte_url}</a> : v.fonte_nome} · consulta em {v.data_consulta}
        </div>
        <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 8, lineHeight: 1.5 }}>Faixa estimada com base em dados públicos de mercado. Não constitui avaliação nem promessa de valor do imóvel.</div>
      </div>
    </Sec>
  );
}

export function SBAvaliacao({ token, avaliacao }) {
  const [nota, setNota] = useState(0);
  const [texto, setTexto] = useState("");
  const [enviada, setEnviada] = useState(avaliacao?.enviada || null);
  const [enviando, setEnviando] = useState(false);
  if (!avaliacao?.habilitada) return null;
  const enviar = async () => {
    setEnviando(true);
    const aba = window.open("", "_blank");
    try {
      await axios.post(`${API}/public/signature/${token}/avaliacao`, { nota, depoimento: texto });
      setEnviada({ nota, depoimento: texto });
      if (aba) aba.location.href = GOOGLE_REVIEW; else window.open(GOOGLE_REVIEW, "_blank");
    } catch (e) { if (aba) aba.close(); alert(e.response?.data?.detail || "Não foi possível enviar."); }
    finally { setEnviando(false); }
  };
  const Estrelas = ({ v, onPick }) => (
    <div style={{ display: "flex", gap: 6 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={onPick ? () => onPick(n) : undefined} disabled={!onPick} data-testid={`sb-aval-star-${n}`}
          style={{ background: "none", border: "none", padding: 2, cursor: onPick ? "pointer" : "default" }}>
          <Star size={28} color="#b8935a" fill={n <= v ? "#b8935a" : "transparent"} />
        </button>
      ))}
    </div>
  );
  return (
    <Sec titulo="Sua avaliação" testid="sb-avaliacao">
      {enviada ? (
        <div data-testid="sb-aval-obrigado">
          <Estrelas v={enviada.nota} />
          <p style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 16, color: "var(--ink-2)", marginTop: 12 }}>Agradecemos a sua avaliação. Foi uma honra realizar esta obra.</p>
          <a href={GOOGLE_REVIEW} target="_blank" rel="noreferrer" data-testid="sb-aval-google" style={{ fontSize: 12, color: "var(--gold)" }}>Avaliar também no Google</a>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 0 }}>Como foi a sua experiência com a CAMASA nesta obra?</p>
          <Estrelas v={nota} onPick={setNota} />
          <textarea value={texto} onChange={e => setTexto(e.target.value)} maxLength={1500} rows={4} placeholder="Conte, se desejar, como foi a experiência (opcional)" data-testid="sb-aval-depoimento"
            style={{ width: "100%", marginTop: 14, background: "var(--bg-2)", border: "1px solid var(--line)", color: "var(--ink)", padding: 12, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
          <button className="btn btn-primary" onClick={enviar} disabled={!nota || enviando} style={{ marginTop: 12 }} data-testid="sb-aval-enviar">{enviando ? "Enviando…" : "Enviar avaliação"}</button>
        </div>
      )}
    </Sec>
  );
}
